/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { getBlobStoreUrl } from 'src/languageStudio/core/utils/demosUtils';

export type AllFilesViewTableProps = TableProps<CustomTextDocumentMetadata> & {
    showLoadMoreFiles: boolean;
    isLoadingMoreFiles: boolean;
    handleLoadMoreFiles: () => void;
    projectKind: CustomTextProjectKind;
    onDocumentClick: (name: string) => void;
};

const messages = defineMessages({
    documentName: {
        id: 'customText.allFilesViewTable.documentName',
        defaultMessage: 'Document name'
    },
    taggedAs: {
        id: 'customText.allFilesViewTable.taggedAs',
        defaultMessage: 'Labeled as'
    },
    dataset: {
        id: 'customText.allFilesViewTable.dataset',
        defaultMessage: 'Dataset'
    },
    emptyTablePlaceholder: {
        id: 'customText.allFilesViewTable.emptyTablePlaceholder',
        defaultMessage: 'No documents found. You need to add documents in'
    },
    training: {
        id: 'customText.allFilesViewTable.training',
        defaultMessage: 'Training'
    },
    testing: {
        id: 'customText.allFilesViewTable.testing',
        defaultMessage: 'Testing'
    },
    loadMoreDocuments: {
        id: 'customText.allFilesViewTable.loadMoreDocuments',
        defaultMessage: 'Load more documents'
    },
    noStorageAccount: {
        id: 'customText.allFilesViewTable.noStorageAccount',
        defaultMessage: 'You don`t have storage account linked to this resource.'
    }
});

const { styleDiv, styleComponent } = getStylistV2('AllFilesViewTable');
const TaggedAsWrapper = styleDiv('TaggedAsWrapper', {
    display: 'flex',
    whiteSpace: 'break-spaces',
    flexWrap: 'wrap'
});
const LoadMoreLink = styleComponent<ILinkProps>(Link)('LoadMoreLink', (_, cssVars) => ({
    background: cssVars.background,
    display: 'flex',
    alignItems: 'center',
    padding: 30,
    boxSizing: 'border-box',
    cursor: 'pointer'
}));

/**
 * A component listing all documents of the project.
 */
export const AllFilesViewTable = observer((props: AllFilesViewTableProps) => {
    const { sortKey, projectKind, onDocumentClick, showLoadMoreFiles, isLoadingMoreFiles, handleLoadMoreFiles, ...rest } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { csAccountStore } = rootStore.azureStore.session;

    const { currentCustomTextProjectStore } = rootStore;

    const onRenderLabeledAsColumn = (item: CustomTextDocumentMetadata) => (
        <TaggedAsWrapper>
            {item.classLabels.map((classLabel, index) => (
                <LuisText key={`${classLabel.name}-${index}`}>
                    {index === item.classLabels.length - 1 ? classLabel.name : `${classLabel.name},`}
                    &nbsp;
                </LuisText>
            ))}
        </TaggedAsWrapper>
    );

    const getDocumentDataset = (item: CustomTextDocumentMetadata) => {
        switch (item.documentDataset) {
            case 'Test':
                return localize(messages.testing);
            case 'Train':
                return localize(messages.training);
            default:
                return '--';
        }
    };

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 150,
            maxWidth: 300,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.documentName),
            isSorted: sortKey && !!(sortKey.key === 'name'),
            isSortedDescending: sortKey && !!(sortKey.key === 'name') && sortKey?.order === 'desc',
            onRender: (item: CustomTextDocumentMetadata) => (
                <NameCellLink onClick={() => onDocumentClick(item.name)}>{item.name}</NameCellLink>
            )
        }
    ];

    if (projectKind !== 'CustomEntityRecognition' && projectKind !== 'CustomHealthcare') {
        columns.push({
            key: 'taggedAs',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            fieldName: 'classLabels',
            onRender: onRenderLabeledAsColumn,
            name: localize(messages.taggedAs),
            isSorted: sortKey && !!(sortKey.key === 'classLabels'),
            isSortedDescending: sortKey && !!(sortKey.key === 'classLabels') && sortKey?.order === 'desc'
        });
    }

    columns.push({
        minWidth: 150,
        maxWidth: 300,
        key: 'dataset',
        isResizable: true,
        fieldName: 'documentDataset',
        name: localize(messages.dataset),
        isSorted: sortKey && !!(sortKey.key === 'documentDataset'),
        isSortedDescending: sortKey && !!(sortKey.key === 'documentDataset') && sortKey?.order === 'desc',
        onRender: (item: CustomTextDocumentMetadata) => <LuisText>{getDocumentDataset(item)}</LuisText>
    });

    const onRenderFooter = () =>
        showLoadMoreFiles && (
            <LoadMoreLink onClick={handleLoadMoreFiles} disabled={isLoadingMoreFiles}>
                {localize(isLoadingMoreFiles ? globalMessages.loading : messages.loadMoreDocuments)}
                &nbsp;{isLoadingMoreFiles && <Spinner size={SpinnerSize.small} />}
            </LoadMoreLink>
        );

    const getBlobStoreLinkStackElement = () => (
        <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign="center">
            <LuisText>{localize(messages.emptyTablePlaceholder)}</LuisText>
            {csAccountStore.ownedStorageId ? (
                <ExternalLink
                    href={getBlobStoreUrl(csAccountStore.ownedStorageId, currentCustomTextProjectStore.containerName)}
                    styles={{
                        root: {
                            fontSize: '14px'
                        }
                    }}>
                    {`${csAccountStore.name}/${currentCustomTextProjectStore.containerName}`}
                </ExternalLink>
            ) : (
                <LuisText>{localize(messages.noStorageAccount)}</LuisText>
            )}
        </Stack>
    );

    return (
        <Table<CustomTextDocumentMetadata>
            columns={columns}
            onRenderDetailsFooter={onRenderFooter}
            emptyPlaceholder={getBlobStoreLinkStackElement()}
            {...rest}
        />
    );
});
